import React, { useState, useEffect, useCallback, FormEvent } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Globe,
  FileText,
  Search,
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Eye,
  Code2,
  Save,
  CheckCircle2,
  ExternalLink,
  Settings,
  Loader2,
  BarChart3,
  Users,
  Clock,
  ArrowDownRight,
  Link2,
  RefreshCw,
  ChevronDown,
  Settings2,
  Play,
  ToggleLeft,
  ToggleRight,
  Download,
  AlertTriangle,
  Wifi,
  ShieldCheck,
  Scan,
  Wrench,
  Network,
  Sparkles,
  RotateCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ShoppingCart,
  Rocket,
} from "lucide-react";
import {
  sites,
  pages,
  analytics,
  agentTasks,
  scheduledTasks as scheduledTasksApi,
  taskTemplates as processesApi,
  type Site,
  type Page,
  type PageFrontmatter,
  type SiteConfig,
  type GAConnectionStatus,
  type GAProperty,
  type GAOverview,
  type GAMetricRow,
  type GATopPage,
  type GATrafficSource,
  type Task,
  type TaskType,
  type TaskTemplate,
  type SiteTheme,
  type SiteFontStyle,
  seo,
  products as productsApi,
  clusters as clustersApi,
  businesses as businessesApi,
  type Cluster,
  type LinkCheckResult,
  type CrawlCheckResult,
  type SeoAnalysisResult,
  type NavCheckResult,
  type AffiliateFixResult,
  type FullAuditResult,
  type DiscoverResult,
  type ProductSuggestResult,
  affiliateClickStats,
  type AffiliateClickStats,
} from "../lib/api";
import { useChat } from "../contexts/ChatContext";
import { useAuth } from "../contexts/AuthContext.js";
import Modal from "../components/Modal";
import { usePolling } from "../hooks/use-polling";
import ReactMarkdown from "react-markdown";
import { SiteAffiliatesTab } from "../components/SiteAffiliatesTab";
import { PublishingTab } from "../components/PublishingTab.js";
import { RunsTab } from "../components/RunsTab.js";
import BuildProgressTracker from "../components/BuildProgressTracker";
import RegenerationQueueMonitor from "../components/RegenerationQueueMonitor";

// ─── Constants ──────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-500/10 text-green-400",
  inactive: "bg-gray-500/10 text-gray-400",
  pending: "bg-amber-500/10 text-amber-400",
  building: "bg-blue-500/10 text-blue-400",
  error: "bg-red-500/10 text-red-400",
};

const PAGE_SIZE = 50;

type TabKey = "overview" | "content" | "import" | "actions" | "analytics" | "affiliates" | "seo" | "build" | "publishing" | "runs";
type UrlDiscoveryImportResult = { created: number; skipped: number; errors: number; total: number };
type UrlDiscoveryPersistedState = {
  result: DiscoverResult | null;
  importResult: UrlDiscoveryImportResult | null;
  error: string | null;
  useCdx: boolean;
  filter: string;
};

const BASE_TABS: { key: TabKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "content", label: "Content" },
  { key: "import", label: "Import" },
  { key: "actions", label: "Actions" },
  { key: "analytics", label: "Analytics" },
  { key: "affiliates", label: "Affiliates" },
  { key: "seo", label: "SEO" },
  { key: "publishing", label: "Publishing" },
  { key: "runs", label: "Runs" },
];

const CRON_LABELS: Record<string, string> = {
  "0 9 * * 1": "1x/week",
  "0 9 * * 1,4": "2x/week",
  "0 9 * * 1,3,5": "3x/week",
  "0 9 * * 1,2,3,4,5": "Weekdays",
  "0 9 * * *": "Daily",
  "0 9 */2 * *": "Every 2 days",
  "0 9 */3 * *": "Every 3 days",
  "0 9 */5 * *": "Every 5 days",
  "0 9 */7 * *": "Every 7 days",
  "0 9 */10 * *": "Every 10 days",
  "0 9 */14 * *": "Every 14 days",
  "0 9 1 * *": "Monthly",
  "0 9 1,15 * *": "2x/month",
};

const THEME_OPTIONS: { value: SiteTheme; label: string; swatch: string }[] = [
  { value: "forest",   label: "Forest",   swatch: "#1e6b4f" },
  { value: "ocean",    label: "Ocean",    swatch: "#1a5276" },
  { value: "sunset",   label: "Sunset",   swatch: "#c0392b" },
  { value: "slate",    label: "Slate",    swatch: "#34495e" },
  { value: "royal",    label: "Royal",    swatch: "#6c3483" },
  { value: "crimson",  label: "Crimson",  swatch: "#922b21" },
  { value: "midnight", label: "Midnight", swatch: "#1b2631" },
  { value: "sand",     label: "Sand",     swatch: "#7d6608" },
];

const FONT_OPTIONS: { value: SiteFontStyle; label: string; sample: string }[] = [
  { value: "modern",    label: "Modern",    sample: "DM Serif + Inter" },
  { value: "editorial", label: "Editorial", sample: "Playfair + Source Sans" },
  { value: "classic",   label: "Classic",   sample: "Merriweather + Lato" },
  { value: "minimal",   label: "Minimal",   sample: "Jakarta Sans" },
  { value: "technical", label: "Technical", sample: "Space Grotesk + IBM Plex" },
];

function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

// ─── Analytics sub-components ──────────────────────────────

function MiniBarChart({ data, dataKey, color }: { data: GAMetricRow[]; dataKey: keyof GAMetricRow; color: string }) {
  if (data.length === 0) return null;
  const values = data.map((d) => Number(d[dataKey]));
  const max = Math.max(...values, 1);

  return (
    <div className="flex items-end gap-px h-32 mt-2">
      {data.map((d, i) => {
        const val = Number(d[dataKey]);
        const pct = (val / max) * 100;
        const dateStr = d.date ? `${d.date.slice(4, 6)}/${d.date.slice(6, 8)}` : "";
        return (
          <div key={i} className="flex-1 group relative" style={{ minWidth: 0 }}>
            <div className={`w-full rounded-t-sm ${color} opacity-80 hover:opacity-100 transition-opacity`} style={{ height: `${Math.max(pct, 2)}%` }} />
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10">
              <div className="bg-gray-800 text-gray-200 text-xs px-2 py-1 rounded whitespace-nowrap shadow-lg">
                {dateStr}: {val.toLocaleString()}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }: { icon: React.ElementType; label: string; value: string; sub?: string; color: string }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-sm text-gray-400">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-100">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function fmtDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

// ─── SEO Tab ──────────────────────────────────────────────

function formatFullAuditResult(data: FullAuditResult): string {
  const analyticsIssues = data.analyticsCheck?.issues?.length ?? 0;
  const deadPagesCount = data.deadPages?.total ?? 0;
  const totalIssues =
    (data.linkCheck?.issues?.length ?? 0) +
    (data.healthCheck?.issues?.length ?? 0) +
    (data.navCheck?.missingPages?.length ?? 0) +
    analyticsIssues +
    deadPagesCount;

  let md = totalIssues === 0
    ? "## All checks passed!\n\n"
    : `## ${totalIssues} issues found\n\n`;

  md += `**Links:** ${data.linkCheck?.stats?.broken ?? 0} broken / ${data.linkCheck?.stats?.total ?? 0} total\n`;
  md += `**Health:** ${data.healthCheck?.summary?.critical ?? 0} critical, ${data.healthCheck?.summary?.warning ?? 0} warnings\n`;
  md += `**Navigation:** ${data.navCheck?.missingPages?.length ?? 0} missing pages, ${data.navCheck?.orphanPages?.length ?? 0} orphan pages\n`;
  md += `**Analytics:** ${data.analyticsCheck?.configured ? "Installed" : `${analyticsIssues} issue${analyticsIssues !== 1 ? "s" : ""}`}`;
  if (data.analyticsCheck?.measurementId) {
    md += ` (${data.analyticsCheck.measurementId})`;
  }
  if (data.analyticsCheck?.liveTagDetected === true) {
    md += ` — verified on live site`;
  } else if (data.analyticsCheck?.liveTagDetected === false) {
    md += ` — NOT detected on live site`;
  }
  md += "\n";
  md += `**Dead pages:** ${deadPagesCount} pages with < 50 words`;
  if (data.deadPages?.deleted) {
    md += ` — **${data.deadPages.deleted} deleted**`;
  }
  md += "\n";

  if (data.healthCheck?.fixed > 0 || (data.deadPages?.deleted ?? 0) > 0) {
    const fixes: string[] = [];
    if (data.healthCheck?.fixed > 0) fixes.push(`${data.healthCheck.fixed} health issues`);
    if (data.deadPages?.deleted) fixes.push(`${data.deadPages.deleted} dead pages`);
    md += `\n**Auto-fixed:** ${fixes.join(", ")}\n`;
  }

  if (analyticsIssues > 0) {
    md += "\n### Analytics Issues\n";
    for (const issue of data.analyticsCheck!.issues) {
      md += `- \`!!\` ${issue}\n`;
    }
  }

  if ((data.linkCheck?.issues?.length ?? 0) > 0) {
    md += "\n### Link Issues\n";
    for (const i of data.linkCheck.issues.slice(0, 10)) {
      const icon = i.severity === "critical" ? "!!!" : i.severity === "warning" ? "!!" : "i";
      md += `- \`${icon}\` **/${i.slug}** — ${i.message}\n`;
    }
    if (data.linkCheck.issues.length > 10) {
      md += `- ... and ${data.linkCheck.issues.length - 10} more\n`;
    }
  }

  if ((data.healthCheck?.issues?.length ?? 0) > 0) {
    md += "\n### Health Issues\n";
    for (const i of data.healthCheck.issues.slice(0, 10)) {
      const icon = i.severity === "critical" ? "!!!" : i.severity === "warning" ? "!!" : "i";
      md += `- \`${icon}\` ${i.message}\n`;
    }
  }

  if (data.deadPages && data.deadPages.total > 0 && data.deadPages.pages) {
    md += `\n### Dead Pages (< 50 words)\n`;
    for (const p of data.deadPages.pages.slice(0, 20)) {
      md += `- \`/${p.slug}\` — ${p.wordCount} words\n`;
    }
    if (data.deadPages.pages.length > 20) {
      md += `- ... and ${data.deadPages.pages.length - 20} more\n`;
    }
  }

  return md;
}

function formatLinkCheckResult(data: LinkCheckResult): string {
  let md = `## Link Integrity Report\n\n`;
  md += `**${data.stats.total}** total links | **${data.stats.valid}** valid | **${data.stats.broken}** broken\n`;

  if (data.issues.length === 0) {
    md += "\nAll links are healthy!";
  } else {
    md += "\n### Issues\n";
    for (const i of data.issues.slice(0, 15)) {
      const icon = i.severity === "critical" ? "!!!" : i.severity === "warning" ? "!!" : "i";
      md += `- \`${icon}\` **/${i.slug}** — ${i.message}${i.target ? ` → \`${i.target}\`` : ""}\n`;
    }
    if (data.issues.length > 15) {
      md += `- ... and ${data.issues.length - 15} more\n`;
    }
  }
  return md;
}

function formatCrawlCheckResult(data: CrawlCheckResult): string {
  if (data.error) return `## Crawl Check\n\n**Error:** ${data.error}`;
  let md = `## Live Crawl Report\n\n**${data.pages?.length ?? 0}** pages crawled\n`;

  if ((data.issues?.length ?? 0) === 0) {
    md += "\nNo issues found!";
  } else {
    md += "\n### Issues\n";
    for (const i of (data.issues ?? []).slice(0, 15)) {
      const icon = i.severity === "critical" ? "!!!" : i.severity === "warning" ? "!!" : "i";
      md += `- \`${icon}\` ${i.message} — ${i.url}\n`;
    }
    if ((data.issues?.length ?? 0) > 15) {
      md += `- ... and ${(data.issues?.length ?? 0) - 15} more\n`;
    }
  }
  return md;
}

function formatSeoAnalysisResult(data: SeoAnalysisResult): string {
  if (data.error) return `## SEO Analysis\n\n**Error:** ${data.error}`;
  let md = `## SEO Analysis Report\n\n`;
  if (data.overallScore != null) {
    md += `**Overall Score: ${data.overallScore}/100**\n\n`;
  }
  if ((data.pages ?? []).length > 0) {
    md += "| Page | Score | Issues |\n|------|-------|--------|\n";
    for (const p of (data.pages ?? []).slice(0, 20)) {
      const scoreLabel = p.score >= 70 ? `${p.score}` : p.score >= 40 ? `${p.score}` : `${p.score}`;
      md += `| /${p.slug} | ${scoreLabel}/100 | ${p.issues.length > 0 ? p.issues.join(", ") : "None"} |\n`;
    }
  }
  return md;
}

function formatNavCheckResult(data: NavCheckResult): string {
  let md = `## Navigation Audit\n\n`;
  md += `**${data.suggestedNav.length}** suggested nav items\n`;
  if (data.orphanPages.length > 0) {
    md += `**${data.orphanPages.length}** orphan pages (no links pointing to them)\n`;
  }
  if (data.missingPages.length > 0) {
    md += `**${data.missingPages.length}** missing expected pages\n`;
  }
  md += `\n### Suggested Navigation\n`;
  md += data.suggestedNav.map(n => `- [${n.label}](${n.href})`).join("\n") + "\n";
  if (data.orphanPages.length > 0) {
    md += `\n### Orphan Pages\n`;
    md += data.orphanPages.map(p => `- /${p}`).join("\n") + "\n";
  }
  if (data.missingPages.length > 0) {
    md += `\n### Missing Pages\n`;
    md += data.missingPages.map(p => `- /${p}`).join("\n") + "\n";
  }
  return md;
}

function formatAffiliateFixResult(data: AffiliateFixResult): string {
  if (data.fixed === 0) return "## Fix Affiliate Links\n\nAll pages already have affiliate links configured!";
  let md = `## Fix Affiliate Links\n\n**Fixed ${data.fixed} pages**\n\n`;
  for (const p of data.pages.slice(0, 20)) {
    md += `- **/${p.slug}** — added ${p.productsAdded.length} products: ${p.productsAdded.join(", ")}\n`;
  }
  if (data.pages.length > 20) {
    md += `- ... and ${data.pages.length - 20} more\n`;
  }
  return md;
}

function formatGSCSetupResult(data: { siteUrl: string; propertyAdded: boolean; sitemapSubmitted: boolean; sitemapUrl: string; error?: string }): string {
  let md = "## Google Search Console Setup\n\n";
  md += `**Property:** ${data.siteUrl}\n`;
  md += `- Property added: ${data.propertyAdded ? "Yes (new)" : "Already exists"}\n`;
  md += `- Sitemap submitted: ${data.sitemapSubmitted ? "Yes" : "No"}\n`;
  md += `- Sitemap URL: ${data.sitemapUrl}\n`;
  if (data.error) {
    md += `\n> **Error:** ${data.error}\n`;
  }
  if (data.sitemapSubmitted) {
    md += "\nGoogle will begin crawling your sitemap shortly. Pages will appear in Search Console within minutes to days.\n";
  }
  return md;
}

function formatIndexingResult(data: { total: number; succeeded: number; failed: number; results: { url: string; status: string; error?: string }[]; truncated?: boolean }): string {
  let md = "## Indexing Request Results\n\n";
  md += `**${data.succeeded}** / ${data.total} URLs submitted successfully\n\n`;
  if (data.failed > 0) {
    md += `**${data.failed} failures:**\n`;
    for (const r of data.results.filter((r) => r.status === "error").slice(0, 10)) {
      md += `- ${r.url} — ${r.error ?? "Unknown error"}\n`;
    }
    md += "\n";
  }
  if (data.succeeded > 0) {
    md += "**Submitted URLs:**\n";
    for (const r of data.results.filter((r) => r.status === "ok").slice(0, 20)) {
      md += `- ${r.url}\n`;
    }
    if (data.results.filter((r) => r.status === "ok").length > 20) {
      md += `- ... and ${data.results.filter((r) => r.status === "ok").length - 20} more\n`;
    }
  }
  if (data.truncated) {
    md += `\n> Note: Only first 200 of ${data.total} URLs were submitted. Run again for remaining.\n`;
  }
  md += "\nGoogle will process indexing requests within minutes to hours. Check Search Console for status.\n";
  return md;
}

function formatProductSuggestResult(data: ProductSuggestResult): string {
  if (!data.products.length) {
    return `No products found for query: **${data.query}**\n\nNetworks searched: ${data.networksSearched.join(", ")}`;
  }
  let md = `## Suggested Products for "${data.query}"\n\n`;
  md += `_Searched: ${data.networksSearched.join(", ")} · ${data.products.length} result${data.products.length !== 1 ? "s" : ""}_\n\n`;
  for (const p of data.products) {
    md += `### ${p.rank}. ${p.title}${p.isPromoted ? " ⭐ Promoted" : ""}\n`;
    md += `- **Network:** ${p.network}${p.merchantName ? ` · ${p.merchantName}` : ""}`;
    if (p.price) md += ` · **Price:** ${p.price}`;
    if (p.category) md += ` · **Category:** ${p.category}`;
    md += "\n";
    if (p.availability) md += `- **Availability:** ${p.availability}\n`;
    if (p.description) md += `- ${p.description.slice(0, 120)}${p.description.length > 120 ? "..." : ""}\n`;
    md += `- **productRef:** \`${p.productRef}\`\n`;
    if (p.affiliateUrl) md += `- [View product](${p.affiliateUrl})\n`;
    md += "\n";
  }
  md += "---\n_Copy the `productRef` values into your page frontmatter `products:` array._\n";
  return md;
}

// ─── SEO Tab Component ────────────────────────────────────

function SeoTab({ siteId, domain }: { siteId: string; domain: string }) {
  const { startSessionLoading, appendLoadingToSession, resolveSession, findSessionByTitle } = useChat();
  const [loadingKey, setLoadingKey] = useState<string | null>(null);

  const runSeoAction = async (key: string, label: string) => {
    const runner = ACTION_RUNNERS[key];
    if (!runner) return;
    setLoadingKey(key);
    const sessionTitle = `${domain} — ${label}`;
    const existing = findSessionByTitle(sessionTitle);
    let sessionId: string;
    let messageId: string | undefined;
    if (existing) {
      sessionId = existing.id;
      messageId = appendLoadingToSession(existing.id);
    } else {
      sessionId = startSessionLoading({ title: sessionTitle });
    }
    try {
      const result = await runner(siteId);
      resolveSession(sessionId, result, messageId);
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : "Action failed";
      resolveSession(sessionId, `## Error\n\n${errMsg}`, messageId);
    } finally {
      setLoadingKey(null);
    }
  };

  const seoActions: Array<{ key: string; label: string; description: string; icon: React.ReactNode; color: string }> = [
    {
      key: "seo.fullAudit",
      label: "Full Audit + Fix",
      description: "Run all checks and auto-fix issues",
      icon: <ShieldCheck className="w-4 h-4" />,
      color: "text-violet-400 bg-violet-500/10 border-violet-500/20",
    },
    {
      key: "seo.linkCheck",
      label: "Link Integrity",
      description: "Check for broken internal links",
      icon: <Link2 className="w-4 h-4" />,
      color: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    },
    {
      key: "seo.crawlCheck",
      label: "Live Crawl",
      description: "Crawl live site for SEO issues",
      icon: <Scan className="w-4 h-4" />,
      color: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
    },
    {
      key: "seo.seoAnalysis",
      label: "SEO Analysis",
      description: "DataForSEO page-level analysis",
      icon: <Search className="w-4 h-4" />,
      color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    },
    {
      key: "seo.navCheck",
      label: "Nav Audit",
      description: "Check navigation structure",
      icon: <Network className="w-4 h-4" />,
      color: "text-amber-400 bg-amber-500/10 border-amber-500/20",
    },
    {
      key: "seo.fixAffiliates",
      label: "Fix Affiliates",
      description: "Add missing affiliate links",
      icon: <Wrench className="w-4 h-4" />,
      color: "text-orange-400 bg-orange-500/10 border-orange-500/20",
    },
    {
      key: "seo.gscSetup",
      label: "GSC Setup",
      description: "Register property + submit sitemap",
      icon: <Globe className="w-4 h-4" />,
      color: "text-green-400 bg-green-500/10 border-green-500/20",
    },
    {
      key: "seo.requestIndexing",
      label: "Request Indexing",
      description: "Submit pages to Google index",
      icon: <Rocket className="w-4 h-4" />,
      color: "text-pink-400 bg-pink-500/10 border-pink-500/20",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Manual SEO Actions */}
      <div>
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Manual Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {seoActions.map(({ key, label, description, icon, color }) => (
            <button
              key={key}
              onClick={() => void runSeoAction(key, label)}
              disabled={loadingKey === key}
              className={`flex items-start gap-3 p-3.5 border rounded-xl text-left transition-all hover:brightness-110 disabled:opacity-50 ${color}`}
            >
              <div className="shrink-0 mt-0.5">{loadingKey === key ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}</div>
              <div>
                <div className="text-sm font-medium">{label}</div>
                <div className="text-xs opacity-70 mt-0.5">{description}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Action dispatch map ──────────────────────────────────

type ActionRunner = (siteId: string) => Promise<string>;

const ACTION_RUNNERS: Record<string, ActionRunner> = {
  "seo.fullAudit": async (siteId) => {
    const res = await seo.fullAudit(siteId, { fix: true, checkLive: true });
    return formatFullAuditResult(res.data);
  },
  "seo.linkCheck": async (siteId) => {
    const res = await seo.linkCheck(siteId);
    return formatLinkCheckResult(res.data);
  },
  "seo.crawlCheck": async (siteId) => {
    const res = await seo.crawlCheck(siteId);
    return formatCrawlCheckResult(res.data);
  },
  "seo.seoAnalysis": async (siteId) => {
    const res = await seo.seoAnalysis(siteId);
    return formatSeoAnalysisResult(res.data);
  },
  "seo.navCheck": async (siteId) => {
    const res = await seo.navCheck(siteId);
    return formatNavCheckResult(res.data);
  },
  "seo.fixAffiliates": async (siteId) => {
    const res = await seo.fixAffiliates(siteId);
    return formatAffiliateFixResult(res.data);
  },
  "seo.gscSetup": async (siteId) => {
    const res = await seo.gscSetup(siteId);
    return formatGSCSetupResult(res.data);
  },
  "seo.requestIndexing": async (siteId) => {
    const res = await seo.requestIndexing(siteId);
    return formatIndexingResult(res.data);
  },
  "site.buildWebsite": async (siteId) => {
    const res = await agentTasks.buildWebsite(siteId);
    return `<!-- BUILD_TASK:${res.data._id} -->\n` +
      `## Build Website Started\n` +
      `**${res.data.title}**`;
  },
  "site.fixSite": async (siteId) => {
    const res = await agentTasks.fixSite(siteId);
    return `<!-- BUILD_TASK:${res.data._id} -->\n` +
      `## Fix Site Started\n` +
      `**${res.data.title}**`;
  },
  "products.suggest": async (siteId) => {
    const keyword = window.prompt("Enter a keyword or topic to find products for:");
    if (!keyword?.trim()) return "Product search cancelled.";
    const context = window.prompt("Optional: add context (e.g. page title or description):");
    const res = await productsApi.suggest(siteId, keyword.trim(), context?.trim() ? { context: context.trim() } : undefined);
    return formatProductSuggestResult(res.data);
  },
};

// Map icon name strings to lucide components
const ICON_MAP: Record<string, React.ReactNode> = {
  ShieldCheck: <ShieldCheck className="w-5 h-5" />,
  Link2: <Link2 className="w-5 h-5" />,
  Scan: <Scan className="w-5 h-5" />,
  Search: <Search className="w-5 h-5" />,
  Globe: <Globe className="w-5 h-5" />,
  Settings2: <Settings2 className="w-5 h-5" />,
  Wrench: <Wrench className="w-5 h-5" />,
  Play: <Play className="w-5 h-5" />,
  RefreshCw: <RefreshCw className="w-5 h-5" />,
  ArrowUpDown: <ArrowUpDown className="w-5 h-5" />,
  ShoppingCart: <ShoppingCart className="w-5 h-5" />,
};

// Map color name to tailwind classes
function colorClasses(color?: string): { card: string; button: string } {
  const map: Record<string, { card: string; button: string }> = {
    purple: { card: "bg-purple-500/10 text-purple-400 border-purple-500/20", button: "bg-purple-600 hover:bg-purple-500" },
    blue: { card: "bg-blue-500/10 text-blue-400 border-blue-500/20", button: "bg-blue-600 hover:bg-blue-500" },
    orange: { card: "bg-orange-500/10 text-orange-400 border-orange-500/20", button: "bg-orange-600 hover:bg-orange-500" },
    green: { card: "bg-green-500/10 text-green-400 border-green-500/20", button: "bg-green-600 hover:bg-green-500" },
    cyan: { card: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20", button: "bg-cyan-600 hover:bg-cyan-500" },
    amber: { card: "bg-amber-500/10 text-amber-400 border-amber-500/20", button: "bg-amber-600 hover:bg-amber-500" },
    red: { card: "bg-red-500/10 text-red-400 border-red-500/20", button: "bg-red-600 hover:bg-red-500" },
  };
  return map[color ?? "blue"] ?? map["blue"]!;
}

// Category display config
const CATEGORY_LABELS: Record<string, string> = {
  audit: "Audits",
  fix: "Fixes",
  analysis: "Analysis",
  content: "Content",
  deploy: "Deploy",
  general: "General",
};

// ─── Import Tab ───────────────────────────────────────────

type ImportStatus = "idle" | "running" | "done" | "error";
type ImportMethod = "xml" | "api" | "discover";

interface ImportResult {
  imported: number;
  skipped?: number;
  skippedEmpty?: number;
  skippedDuplicate?: number;
  errors?: string[];
  totalInFile?: number;
  /** Total items available on the source (from WP X-WP-Total header) */
  totalSource?: number;
  /** Items processed so far (imported + skipped) */
  processed?: number;
}

function safeLoadJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : fallback;
  } catch {
    return fallback;
  }
}

function ImportTab({
  siteId,
  domain,
  sourceUrl: initialSourceUrl,
  onImportDone,
}: {
  siteId: string;
  domain: string;
  sourceUrl?: string;
  onImportDone: () => void;
}) {
  const importMethodStorageKey = `domain-detail:${siteId}:import-method`;
  const [method, setMethod] = useState<ImportMethod>(() =>
    safeLoadJson<ImportMethod>(importMethodStorageKey, "xml")
  );

  // Shared state
  const [importPosts, setImportPosts] = useState(true);
  const [importWPPages, setImportWPPages] = useState(true);
  const [status, setStatus] = useState<ImportStatus>("idle");
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // XML-specific
  const [file, setFile] = useState<File | null>(null);

  // API-specific
  const [sourceUrl, setSourceUrl] = useState(initialSourceUrl || `https://${domain}`);
  const [username, setUsername] = useState("");
  const [appPassword, setAppPassword] = useState("");
  const [limit, setLimit] = useState(0);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [credsSaved, setCredsSaved] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(importMethodStorageKey, JSON.stringify(method));
  }, [importMethodStorageKey, method]);

  // Poll task for API import progress
  useEffect(() => {
    if (!taskId || status !== "running") return;
    const interval = setInterval(async () => {
      try {
        const res = await agentTasks.get(taskId);
        const task = res.data;
        const taskResult = task.result as Record<string, unknown> | undefined;
        if (taskResult) {
          setResult({
            imported: (taskResult.imported as number) ?? 0,
            skipped: (taskResult.skipped as number) ?? 0,
            skippedEmpty: (taskResult.skippedEmpty as number) ?? 0,
            skippedDuplicate: (taskResult.skippedDuplicate as number) ?? 0,
            errors: (taskResult.errors as string[]) ?? [],
            totalSource: (taskResult.total as number) ?? undefined,
            processed: (taskResult.processed as number) ?? undefined,
          });
        }
        if (task.status === "done") {
          setStatus("done");
          onImportDone();
          clearInterval(interval);
        } else if (task.status === "failed") {
          setStatus("error");
          setError(task.error || "Import failed");
          clearInterval(interval);
        }
      } catch {
        // ignore polling errors
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [taskId, status, onImportDone]);

  async function handleXmlImport(e: FormEvent) {
    e.preventDefault();
    if (!file) return;

    const contentTypes: string[] = [];
    if (importPosts) contentTypes.push("post");
    if (importWPPages) contentTypes.push("page");
    if (contentTypes.length === 0) return;

    setStatus("running");
    setError(null);
    setResult(null);

    try {
      const res = await agentTasks.importWPXml(siteId, file, contentTypes);
      setResult(res.data);
      setStatus("done");
      onImportDone();
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Import failed");
    }
  }

  async function handleApiImport(e: FormEvent) {
    e.preventDefault();
    if (!sourceUrl) return;

    const contentTypes: ("posts" | "pages")[] = [];
    if (importPosts) contentTypes.push("posts");
    if (importWPPages) contentTypes.push("pages");
    if (contentTypes.length === 0) return;

    setStatus("running");
    setError(null);
    setResult(null);

    try {
      const res = await agentTasks.importWP(siteId, {
        sourceUrl,
        username: username || undefined,
        appPassword: appPassword || undefined,
        contentTypes,
        limit,
      });
      setTaskId(res.data._id);
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Failed to start import");
    }
  }

  function handleReset() {
    setStatus("idle");
    setResult(null);
    setError(null);
    setFile(null);
    setTaskId(null);
  }

  async function handleSaveCreds() {
    try {
      await sites.update(siteId, {
        config: {
          sourceUrl,
          wpUsername: username || undefined,
          wpAppPassword: appPassword || undefined,
        } as unknown as import("../lib/api").SiteConfig,
      });
      setCredsSaved(true);
    } catch {
      alert("Failed to save credentials");
    }
  }

  // Load saved credentials + check for in-progress import tasks on mount
  useEffect(() => {
    sites.get(siteId).then((res) => {
      const cfg = res.data?.config as unknown as Record<string, unknown> | undefined;
      if (cfg?.sourceUrl) setSourceUrl(cfg.sourceUrl as string);
      if (cfg?.wpUsername) setUsername(cfg.wpUsername as string);
      if (cfg?.wpAppPassword) setAppPassword(cfg.wpAppPassword as string);
    }).catch(() => {});

    // Resume polling if there's an in-progress import task
    agentTasks.list(siteId, { status: "in_progress" }).then((res) => {
      const running = res.data.find((t) => {
        const ctx = t.context as Record<string, unknown> | undefined;
        return ctx?.trigger === "import-wp";
      });
      if (running) {
        setTaskId(running._id);
        setStatus("running");
        setMethod("api");
        // Restore partial progress from task result
        const r = running.result as Record<string, unknown> | undefined;
        if (r) {
          setResult({
            imported: (r.imported as number) ?? 0,
            skipped: (r.skipped as number) ?? 0,
            totalSource: (r.total as number) ?? undefined,
            processed: (r.processed as number) ?? undefined,
          });
        }
      }
    }).catch(() => {});
  }, [siteId]);

  const isRunning = status === "running";

  return (
    <div className="space-y-6">
      {/* Method selector */}
      <div className="flex gap-2">
        <button
          onClick={() => !isRunning && setMethod("xml")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${method === "xml" ? "bg-brand-600 text-white" : "bg-gray-800 text-gray-400 hover:text-gray-200"}`}
        >
          Upload XML Export
        </button>
        <button
          onClick={() => !isRunning && setMethod("api")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${method === "api" ? "bg-brand-600 text-white" : "bg-gray-800 text-gray-400 hover:text-gray-200"}`}
        >
          WP REST API
        </button>
        <button
          onClick={() => !isRunning && setMethod("discover")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${method === "discover" ? "bg-brand-600 text-white" : "bg-gray-800 text-gray-400 hover:text-gray-200"}`}
        >
          Discover Old URLs
        </button>
      </div>

      {method !== "discover" && (
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
        {method === "xml" ? (
          <>
            <h3 className="text-lg font-semibold text-gray-100 mb-1">Import from XML Export</h3>
            <p className="text-sm text-gray-500 mb-6">
              In WordPress, go to <span className="text-gray-300">Tools → Export</span>, select Posts or Pages, and download the file.
            </p>
            <form onSubmit={handleXmlImport} className="space-y-4">
              {/* File upload */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Export File (.xml)</label>
                <label className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${file ? "border-brand-500/50 bg-brand-500/5" : "border-gray-700 hover:border-gray-600"} ${isRunning ? "opacity-50 pointer-events-none" : ""}`}>
                  <input
                    type="file"
                    accept=".xml"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    disabled={isRunning}
                    className="hidden"
                  />
                  {file ? (
                    <>
                      <FileText className="w-4 h-4 text-brand-400" />
                      <span className="text-sm text-gray-200">{file.name}</span>
                      <span className="text-xs text-gray-500">({(file.size / 1024).toFixed(0)} KB)</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-400">Click to select XML file</span>
                    </>
                  )}
                </label>
              </div>

              {/* Shared: content types */}
              <ImportContentTypeCheckboxes
                importPosts={importPosts}
                setImportPosts={setImportPosts}
                importPages={importWPPages}
                setImportPages={setImportWPPages}
                disabled={isRunning}
              />

              <ImportSubmitButton
                disabled={isRunning || !file || (!importPosts && !importWPPages)}
                isRunning={isRunning}
                isDone={status === "done"}
                onReset={handleReset}
              />
            </form>
          </>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-gray-100 mb-1">Import via WP REST API</h3>
            <p className="text-sm text-gray-500 mb-6">
              Pull content directly from a live WordPress site. Credentials are optional for public sites, but required for drafts and private content.
            </p>

            {/* Saved credentials section */}
            <div className="mb-6 p-4 bg-gray-800/50 border border-gray-700 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-300">WP Credentials</span>
                {credsSaved && <span className="text-xs text-green-400">Saved</span>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Site URL</label>
                  <input
                    type="url"
                    value={sourceUrl}
                    onChange={(e) => { setSourceUrl(e.target.value); setCredsSaved(false); }}
                    placeholder="https://example.com"
                    disabled={isRunning}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-200 text-sm focus:border-brand-500 focus:outline-none disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => { setUsername(e.target.value); setCredsSaved(false); }}
                    placeholder="admin"
                    disabled={isRunning}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-200 text-sm focus:border-brand-500 focus:outline-none disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">App Password</label>
                  <input
                    type="password"
                    value={appPassword}
                    onChange={(e) => { setAppPassword(e.target.value); setCredsSaved(false); }}
                    placeholder="xxxx xxxx xxxx xxxx"
                    disabled={isRunning}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-200 text-sm focus:border-brand-500 focus:outline-none disabled:opacity-50"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleSaveCreds}
                disabled={isRunning || !sourceUrl}
                className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors disabled:opacity-50"
              >
                <Save className="w-3 h-3 inline mr-1" />
                Save Credentials
              </button>
            </div>

            <form onSubmit={handleApiImport} className="space-y-4">
              {/* Content types + limit */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ImportContentTypeCheckboxes
                  importPosts={importPosts}
                  setImportPosts={setImportPosts}
                  importPages={importWPPages}
                  setImportPages={setImportWPPages}
                  disabled={isRunning}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Max Items <span className="text-gray-600">(0 = all)</span></label>
                  <input
                    type="number"
                    value={limit}
                    onChange={(e) => setLimit(parseInt(e.target.value, 10) || 0)}
                    min={0}
                    disabled={isRunning}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-200 text-sm focus:border-brand-500 focus:outline-none disabled:opacity-50"
                  />
                </div>
              </div>

              <ImportSubmitButton
                disabled={isRunning || !sourceUrl || (!importPosts && !importWPPages)}
                isRunning={isRunning}
                isDone={status === "done"}
                onReset={handleReset}
              />
            </form>
          </>
        )}
      </div>
      )}

      {method === "discover" && (
        <UrlDiscoveryPanel siteId={siteId} domain={domain} />
      )}

      {/* Progress (API import polling) */}
      {status === "running" && method === "api" && result && (
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
          <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-brand-400" />
            Import in progress
            {result.totalSource ? (
              <span className="text-xs text-gray-500 ml-auto">
                {result.processed ?? 0} / {result.totalSource} processed
              </span>
            ) : null}
          </h4>
          {result.totalSource ? (
            <div className="w-full bg-gray-800 rounded-full h-2 mb-4">
              <div
                className="bg-brand-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, ((result.processed ?? 0) / result.totalSource) * 100)}%` }}
              />
            </div>
          ) : null}
          <ImportResultGrid result={result} />
        </div>
      )}

      {/* Done */}
      {status === "done" && result && (
        <div className="bg-gray-900/50 border border-green-900/50 rounded-xl p-6">
          <h4 className="text-sm font-medium text-green-400 mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Import Complete
          </h4>
          <ImportResultGrid result={result} />
          {result.errors?.length ? (
            <div className="mt-4 text-xs text-red-400/80 max-h-32 overflow-y-auto space-y-1">
              {result.errors.map((err, i) => (
                <div key={i}>• {err}</div>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {/* Error */}
      {status === "error" && (
        <div className="bg-gray-900/50 border border-red-900/50 rounded-xl p-6">
          <h4 className="text-sm font-medium text-red-400 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Import Failed
          </h4>
          <p className="text-sm text-red-400/80">{error}</p>
        </div>
      )}
    </div>
  );
}

function ImportContentTypeCheckboxes({ importPosts, setImportPosts, importPages, setImportPages, disabled }: {
  importPosts: boolean; setImportPosts: (v: boolean) => void;
  importPages: boolean; setImportPages: (v: boolean) => void;
  disabled: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-400 mb-2">Content Types</label>
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
          <input type="checkbox" checked={importPosts} onChange={(e) => setImportPosts(e.target.checked)} disabled={disabled} className="rounded border-gray-600 bg-gray-800 text-brand-500" />
          Posts
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
          <input type="checkbox" checked={importPages} onChange={(e) => setImportPages(e.target.checked)} disabled={disabled} className="rounded border-gray-600 bg-gray-800 text-brand-500" />
          Pages
        </label>
      </div>
    </div>
  );
}

function ImportSubmitButton({ disabled, isRunning, isDone, onReset }: {
  disabled: boolean; isRunning: boolean; isDone: boolean; onReset: () => void;
}) {
  return (
    <div className="flex items-center gap-4 pt-2">
      <button
        type="submit"
        disabled={disabled}
        className="px-5 py-2.5 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
      >
        {isRunning ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Importing...</>
        ) : (
          <><Download className="w-4 h-4" /> Import</>
        )}
      </button>
      {isDone && (
        <button type="button" onClick={onReset} className="px-4 py-2.5 text-sm text-gray-400 hover:text-gray-200 transition-colors">
          Reset
        </button>
      )}
    </div>
  );
}

function ImportResultGrid({ result }: { result: ImportResult }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
      {result.totalInFile != null && (
        <div>
          <div className="text-2xl font-bold text-gray-400">{result.totalInFile}</div>
          <div className="text-xs text-gray-500">In File</div>
        </div>
      )}
      <div>
        <div className="text-2xl font-bold text-green-400">{result.imported}</div>
        <div className="text-xs text-gray-500">Imported</div>
      </div>
      <div>
        <div className="text-2xl font-bold text-yellow-400">{result.skippedEmpty ?? 0}</div>
        <div className="text-xs text-gray-500">Empty</div>
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-400">{result.skippedDuplicate ?? 0}</div>
        <div className="text-xs text-gray-500">Duplicates</div>
      </div>
      <div>
        <div className="text-2xl font-bold text-red-400">{result.errors?.length ?? 0}</div>
        <div className="text-xs text-gray-500">Errors</div>
      </div>
    </div>
  );
}

function UrlDiscoveryPanel({ siteId, domain }: { siteId: string; domain: string }) {
  const storageKey = `domain-detail:${siteId}:url-discovery`;
  const persisted = safeLoadJson<UrlDiscoveryPersistedState>(storageKey, {
    result: null,
    importResult: null,
    error: null,
    useCdx: false,
    filter: "",
  });
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<UrlDiscoveryImportResult | null>(persisted.importResult);
  const [result, setResult] = useState<DiscoverResult | null>(persisted.result);
  const [error, setError] = useState<string | null>(persisted.error);
  const [useCdx, setUseCdx] = useState(persisted.useCdx);
  const [filter, setFilter] = useState(persisted.filter);

  useEffect(() => {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        result,
        importResult,
        error,
        useCdx,
        filter,
      } satisfies UrlDiscoveryPersistedState)
    );
  }, [storageKey, result, importResult, error, useCdx, filter]);

  async function handleDiscover() {
    setLoading(true);
    setError(null);
    setResult(null);
    setImportResult(null);
    try {
      const res = await sites.discoverUrls(siteId, { cdx: useCdx });
      setResult(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Discovery failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleImportAll() {
    if (!result || result.urls.length === 0) return;
    setImporting(true);
    setError(null);
    setImportResult(null);
    try {
      const slugs = result.urls.map((u) => u.slug);
      const res = await sites.importDiscoveredUrls(siteId, slugs);
      setImportResult(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setImporting(false);
    }
  }

  const filtered = result?.urls.filter((u) =>
    !filter || u.slug.toLowerCase().includes(filter.toLowerCase())
  ) ?? [];

  return (
    <div className="space-y-4">
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-100 mb-1">Discover Historical URLs</h3>
        <p className="text-sm text-gray-500 mb-4">
          Search the Wayback Machine for archived sitemaps and pages from <span className="text-gray-300">{domain}</span>.
          Great for expired domains — find old content URLs that can be rebuilt with fresh content for SEO advantage.
        </p>

        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={handleDiscover}
            disabled={loading}
            className="px-5 py-2.5 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Searching...</>
            ) : (
              <><Search className="w-4 h-4" /> Discover URLs</>
            )}
          </button>
          <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={useCdx}
              onChange={(e) => setUseCdx(e.target.checked)}
              disabled={loading}
              className="rounded border-gray-600 bg-gray-800 text-brand-500"
            />
            Deep scan (CDX API — slower but more thorough)
          </label>
        </div>

        {error && (
          <div className="p-3 bg-red-900/20 border border-red-800/50 rounded-lg text-sm text-red-400">
            {error}
          </div>
        )}
      </div>

      {result && (
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-sm font-medium text-gray-200">
                Found {result.total} historical URL{result.total !== 1 ? "s" : ""} not in your site
              </h4>
              <p className="text-xs text-gray-500 mt-0.5">
                Sources: {result.sources.join(", ")} — URLs already in your DB are filtered out
              </p>
            </div>
            {result.total > 0 && (
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter slugs..."
                  className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-200 placeholder-gray-600 focus:border-brand-500 focus:outline-none w-48"
                />
                <button
                  onClick={handleImportAll}
                  disabled={importing || !!importResult}
                  className="px-4 py-1.5 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  {importing ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Importing...</>
                  ) : importResult ? (
                    <>Imported {importResult.created}</>
                  ) : (
                    <>Import All ({result.total})</>
                  )}
                </button>
              </div>
            )}
          </div>

          {importResult && (
            <div className="mb-4 p-3 bg-green-900/20 border border-green-800/50 rounded-lg text-sm text-green-400 flex items-center gap-4">
              <span>{importResult.created} pages created</span>
              {importResult.skipped > 0 && <span className="text-gray-500">{importResult.skipped} skipped (already exist)</span>}
              {importResult.errors > 0 && <span className="text-red-400">{importResult.errors} errors</span>}
            </div>
          )}

          {filtered.length > 0 ? (
            <div className="max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-500 border-b border-gray-800 sticky top-0 bg-gray-900/90">
                  <tr>
                    <th className="text-left py-2 px-2">Slug</th>
                    <th className="text-left py-2 px-2">Last Seen</th>
                    <th className="text-left py-2 px-2">Source</th>
                    <th className="text-right py-2 px-2">Archive</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/50">
                  {filtered.map((u) => (
                    <tr key={u.slug} className="hover:bg-gray-800/30">
                      <td className="py-1.5 px-2 text-gray-300 font-mono text-xs">/{u.slug}</td>
                      <td className="py-1.5 px-2 text-gray-500 text-xs">{u.lastSeen ?? "—"}</td>
                      <td className="py-1.5 px-2">
                        <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded ${
                          u.source === "sitemap" ? "bg-green-900/30 text-green-400" :
                          u.source === "cdx" ? "bg-blue-900/30 text-blue-400" :
                          "bg-yellow-900/30 text-yellow-400"
                        }`}>
                          {u.source}
                        </span>
                      </td>
                      <td className="py-1.5 px-2 text-right">
                        <a
                          href={`https://web.archive.org/web/2024/${u.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand-400 hover:text-brand-300 text-xs inline-flex items-center gap-1"
                        >
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 py-4 text-center">
              {filter ? "No URLs match your filter" : "No historical URLs found"}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function RegenMonitor({ siteId }: { siteId: string }): React.ReactElement | null {
  return <RegenerationQueueMonitor siteId={siteId} /> as React.ReactElement | null;
}

interface BuildTabProps {
  siteId: string;
  siteConfig: SiteConfig;
  buildError: string | null;
  setBuildError: (v: string | null) => void;
  activeBuildTaskId: string | null;
  loadSite: () => void;
  buildDeploying: boolean;
  setBuildDeploying: (v: boolean) => void;
  buildRegenerating: boolean;
  setBuildRegenerating: (v: boolean) => void;
}

function BuildTab({ siteId, siteConfig, buildError, setBuildError, activeBuildTaskId, loadSite, buildDeploying, setBuildDeploying, buildRegenerating, setBuildRegenerating }: BuildTabProps) {
  return (
    <div className="space-y-4">
      {/* Build error */}
      {buildError && (
        <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
          {buildError}
          <button onClick={() => setBuildError(null)} className="ml-2 text-red-500 hover:text-red-300">&times;</button>
        </div>
      )}

      {/* Active build task */}
      {activeBuildTaskId ? (
        <BuildProgressTracker
          taskId={activeBuildTaskId}
          onComplete={loadSite}
        />
      ) : null}

      <RegenMonitor siteId={siteId} />

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={async () => {
            setBuildDeploying(true);
            setBuildError(null);
            try {
              await agentTasks.astroDeploy(siteId);
              void loadSite();
            } catch (err: unknown) {
              setBuildError(err instanceof Error ? err.message : "Deploy failed");
            } finally {
              setBuildDeploying(false);
            }
          }}
          disabled={buildDeploying}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {buildDeploying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
          {buildDeploying ? "Deploying..." : "Deploy Now"}
        </button>
        <button
          onClick={async () => {
            setBuildRegenerating(true);
            setBuildError(null);
            try {
              await agentTasks.startRegeneration(siteId);
            } catch (err: unknown) {
              setBuildError(err instanceof Error ? err.message : "Failed to start regeneration");
            } finally {
              setBuildRegenerating(false);
            }
          }}
          disabled={buildRegenerating}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {buildRegenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {buildRegenerating ? "Starting..." : "Start Regeneration"}
        </button>
      </div>

      {/* Last deploy info */}
      {Boolean((siteConfig as unknown as Record<string, unknown>)?.astro) && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <p className="text-xs text-gray-500">Last Deploy</p>
          <p className="text-sm text-gray-300 mt-1">
            {((siteConfig as unknown as Record<string, unknown>).astro as Record<string, unknown>)?.lastDeployAt
              ? new Date(((siteConfig as unknown as Record<string, unknown>).astro as Record<string, unknown>).lastDeployAt as string).toLocaleString()
              : "Never deployed"}
          </p>
        </div>
      )}
    </div>
  );
}

function ActionsTab({ siteId, domain }: { siteId: string; domain: string }) {
  const { startSessionLoading, appendLoadingToSession, resolveSession, findSessionByTitle } = useChat();
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [actions, setActions] = useState<TaskTemplate[]>([]);
  const [actionsLoading, setActionsLoading] = useState(true);

  useEffect(() => {
    processesApi.list().then((res) => {
      setActions(res.data);
      setActionsLoading(false);
    }).catch(() => setActionsLoading(false));
  }, []);

  const runAction = async (action: TaskTemplate) => {
    const endpoint = action.actionEndpoint;
    if (!endpoint || !ACTION_RUNNERS[endpoint]) return;

    const key = action._id;
    setLoadingKey(key);

    const sessionTitle = `${domain} — ${action.name}`;
    const existing = findSessionByTitle(sessionTitle);
    let sessionId: string;
    let messageId: string | undefined;

    if (existing) {
      sessionId = existing.id;
      messageId = appendLoadingToSession(existing.id);
    } else {
      sessionId = startSessionLoading({ title: sessionTitle });
    }

    try {
      const result = await ACTION_RUNNERS[endpoint]!(siteId);
      resolveSession(sessionId, result, messageId);
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : "Action failed";
      resolveSession(sessionId, `## Error\n\n${errMsg}`, messageId);
    } finally {
      setLoadingKey(null);
    }
  };

  // Group actions by category
  const grouped = actions.reduce<Record<string, TaskTemplate[]>>((acc, action) => {
    const cat = action.category ?? "general";
    if (!acc[cat]) acc[cat] = [];
    acc[cat]!.push(action);
    return acc;
  }, {});

  // Sort categories in a stable order
  const categoryOrder = ["audit", "analysis", "fix", "content", "deploy", "general"];
  const sortedCategories = Object.keys(grouped).sort(
    (a, b) => categoryOrder.indexOf(a) - categoryOrder.indexOf(b)
  );

  if (actionsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (actions.length === 0) {
    return (
      <div className="text-center py-12 text-zinc-500">
        <p>No actions configured yet.</p>
        <p className="text-xs mt-1">Run the seed script: <code>npx tsx --env-file .env scripts/seed-actions.ts</code></p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-white">Actions</h2>
        <p className="text-sm text-zinc-400 mt-1">
          Run automated processes — results appear in the chat panel
        </p>
      </div>

      {sortedCategories.map((cat) => (
        <div key={cat}>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3">
            {CATEGORY_LABELS[cat] ?? cat}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {grouped[cat]!.map((action) => {
              const colors = colorClasses(action.color);
              const icon = ICON_MAP[action.icon ?? "Play"] ?? <Play className="w-5 h-5" />;
              const hasRunner = !!(action.actionEndpoint && ACTION_RUNNERS[action.actionEndpoint]);

              return (
                <div
                  key={action._id}
                  className={`border rounded-xl p-5 ${colors.card} transition-all`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="mt-0.5">{icon}</div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-white text-sm">{action.name}</h3>
                      <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{action.description}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => runAction(action)}
                    disabled={loadingKey !== null || !hasRunner}
                    className={`w-full mt-2 px-3 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 ${colors.button}`}
                  >
                    {loadingKey === action._id ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Running...
                      </span>
                    ) : (
                      "Run"
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────

export default function DomainDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { startSession } = useChat();
  const { tenant } = useAuth();
  const tenantCap = (tenant as { publishingConfig?: { maxDailyQuota?: number } } | null)?.publishingConfig?.maxDailyQuota ?? 50;

  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [site, setSite] = useState<Site | null>(null);
  const [siteLoading, setSiteLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cluster, setCluster] = useState<Cluster | null>(null);
  const [businessName, setBusinessName] = useState<string | null>(null);

  // ─── Pages state ─────────────────────────────────────────
  const [pageList, setPageList] = useState<Page[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState<"slug" | "title" | "words" | "updated">("slug");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // Page editor
  const [importingPageId, setImportingPageId] = useState<string | null>(null);
  const [editor, setEditor] = useState<{ open: boolean; page: Page | null }>({ open: false, page: null });
  const [editorContent, setEditorContent] = useState("");
  const [editorFrontmatter, setEditorFrontmatter] = useState<PageFrontmatter>({ title: "", description: "", keywords: [] });
  const [editorSlug, setEditorSlug] = useState("");
  const [editorView, setEditorView] = useState<"edit" | "preview" | "split">("split");
  const [saving, setSaving] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);

  // Site edit modal
  const [showSiteEdit, setShowSiteEdit] = useState(false);
  const [siteForm, setSiteForm] = useState<{ status: string; config: SiteConfig } | null>(null);
  const [siteSaving, setSiteSaving] = useState(false);

  // (affiliate overrides managed by SiteAffiliatesTab component)

  // Delete
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ─── Analytics state ─────────────────────────────────────
  const [connStatus, setConnStatus] = useState<GAConnectionStatus | null>(null);
  const [overview, setOverview] = useState<GAOverview | null>(null);
  const [daily, setDaily] = useState<GAMetricRow[]>([]);
  const [topPages, setTopPages] = useState<GATopPage[]>([]);
  const [trafficSources, setTrafficSources] = useState<GATrafficSource[]>([]);
  const [days, setDays] = useState(30);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [clickStats, setClickStats] = useState<AffiliateClickStats | null>(null);

  // Property assignment
  const [showPropertyPicker, setShowPropertyPicker] = useState(false);
  const [gaProperties, setGaProperties] = useState<GAProperty[]>([]);
  const [propsLoading, setPropsLoading] = useState(false);
  const [savingProperty, setSavingProperty] = useState(false);

  // ─── Processes state ─────────────────────────────────────
  const [processList, setProcessList] = useState<TaskTemplate[]>([]);
  const [scheduledList, setScheduledList] = useState<Task[]>([]);
  const [processesLoading, setProcessesLoading] = useState(false);
  const [runningTaskId, setRunningTaskId] = useState<string | null>(null);
  const [processStartDates, setProcessStartDates] = useState<Record<string, string>>({});

  // (site import handled via AI chat — no local state needed)

  // ─── Build tab state ──────────────────────────────────────
  const [showBuildTab, setShowBuildTab] = useState(false);
  const [activeBuildTaskId, setActiveBuildTaskId] = useState<string | null>(null);
  const [buildDeploying, setBuildDeploying] = useState(false);
  const [buildRegenerating, setBuildRegenerating] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);

  // ─── Data loading ────────────────────────────────────────

  useEffect(() => {
    if (id) loadSite();
  }, [id]);

  useEffect(() => {
    if (site) {
      loadPages(1);
      loadProcessesAndTasks();
      // Build tab always visible — all sites deploy via Astro SSG
      setShowBuildTab(true);
      // Check for active build tasks
      agentTasks.list(site._id, { type: "build-website" as TaskType }).then((res) => {
        const active = res.data.find((t) => t.status !== "done" && t.status !== "failed");
        if (active) {
          setActiveBuildTaskId(active._id);
        }
      }).catch(() => {});
    }
  }, [site?._id]);

  // Auto-refresh site stats + page counts every 10s
  usePolling(useCallback(async () => {
    if (!id) return;
    try {
      const res = await sites.get(id);
      setSite(res.data);
      // Refresh page list silently (keep current page)
      if (res.data) {
        const pageRes = await pages.list(res.data._id, currentPage, PAGE_SIZE);
        setPageList(pageRes.data);
        setTotal(pageRes.total);
        setHasMore(pageRes.hasMore);
      }
    } catch {
      // Silent — don't overwrite existing data on transient errors
    }
  }, [id, currentPage]), 10_000);

  async function loadSite() {
    setSiteLoading(true);
    try {
      const res = await sites.get(id!);
      setSite(res.data);
      // Load cluster info if site belongs to one
      if (res.data.clusterId) {
        try {
          const clusterRes = await clustersApi.get(res.data.clusterId);
          setCluster(clusterRes.data);
        } catch {
          setCluster(null);
        }
        // Load business name for breadcrumb
        businessesApi.get(res.data.clusterId)
          .then((bRes) => setBusinessName(bRes.data.name))
          .catch(() => setBusinessName(null));
      } else {
        setCluster(null);
        setBusinessName(null);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load site");
    } finally {
      setSiteLoading(false);
    }
  }

  async function loadPages(page: number) {
    if (!site) return;
    setPagesLoading(true);
    try {
      const res = await pages.list(site._id, page, PAGE_SIZE);
      setPageList(res.data);
      setTotal(res.total);
      setCurrentPage(res.page);
      setHasMore(res.hasMore);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load pages");
    } finally {
      setPagesLoading(false);
    }
  }

  async function loadProcessesAndTasks() {
    if (!site) return;
    setProcessesLoading(true);
    try {
      const [procs, tasks] = await Promise.all([
        processesApi.list(),
        scheduledTasksApi.list(site._id),
      ]);
      setProcessList(procs.data.filter((p) => p.isActive));
      setScheduledList(tasks.data);
    } catch {
      // Ignore — processes/tasks might not exist yet
    } finally {
      setProcessesLoading(false);
    }
  }

  // ─── Analytics loading ───────────────────────────────────

  const loadAnalyticsStatus = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const status = await analytics.status();
      setConnStatus(status);
      return status;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to check analytics connection");
      return null;
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const loadAnalyticsData = useCallback(async (period: number, siteId: string) => {
    setDataLoading(true);
    setError(null);
    try {
      const [ov, dl, tp, ts, clicks] = await Promise.all([
        analytics.overview(period, siteId),
        analytics.daily(period, siteId),
        analytics.topPages(period, siteId),
        analytics.trafficSources(period, siteId),
        affiliateClickStats.get(siteId, period).catch(() => null),
      ]);
      setOverview(ov.data);
      setDaily(dl.data);
      setTopPages(tp.data);
      setTrafficSources(ts.data);
      if (clicks) setClickStats(clicks.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load analytics data");
    } finally {
      setDataLoading(false);
    }
  }, []);

  // Load analytics when tab is activated — auto-provision if needed
  useEffect(() => {
    if (activeTab === "analytics" && site && !connStatus) {
      loadAnalyticsStatus().then(async (status) => {
        if (!status?.connected || !status.accountId) return;
        // If no property, try to auto-provision
        if (!site.config?.gaPropertyId && !status.propertyId) {
          try {
            await analytics.autoProvision(site._id);
            await loadSite(); // reload site to get the new gaPropertyId
          } catch {
            // Silently fail — user can still assign manually
          }
        }
        loadAnalyticsData(days, site._id);
      });
    }
  }, [activeTab, site?._id]);

  // ─── Page handlers ───────────────────────────────────────

  function openCreate() {
    setEditorContent("");
    setEditorFrontmatter({ title: "", description: "", keywords: [] });
    setEditorSlug("");
    setEditorError(null);
    setEditor({ open: true, page: null });
  }

  function openEdit(page: Page) {
    setEditorContent(page.content);
    setEditorFrontmatter(page.frontmatter ?? { title: "", description: "", keywords: [] });
    setEditorSlug(page.slug);
    setEditorError(null);
    setEditor({ open: true, page });
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setEditorError(null);
    try {
      if (editor.page) {
        await pages.update(editor.page._id, { content: editorContent, frontmatter: editorFrontmatter });
      } else {
        await pages.create({ siteId: site!._id, slug: editorSlug, content: editorContent, frontmatter: editorFrontmatter });
      }
      setEditor({ open: false, page: null });
      await loadPages(currentPage);
    } catch (err: unknown) {
      setEditorError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await pages.delete(deleteId);
      setDeleteId(null);
      await loadPages(currentPage);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  // ─── Site settings ───────────────────────────────────────

  function openSiteEdit() {
    if (!site) return;
    setSiteForm({ status: site.status, config: { ...site.config } });
    setShowSiteEdit(true);
  }

  async function handleSiteSave(e: FormEvent) {
    e.preventDefault();
    if (!site || !siteForm) return;
    setSiteSaving(true);
    try {
      await sites.update(site._id, { status: siteForm.status as Site["status"], config: siteForm.config });
      setShowSiteEdit(false);
      await loadSite();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSiteSaving(false);
    }
  }

  // ─── Analytics handlers ──────────────────────────────────

  function handlePeriodChange(newDays: number) {
    setDays(newDays);
    if (site) loadAnalyticsData(newDays, site._id);
  }


  async function openPropertyPicker() {
    setShowPropertyPicker(true);
    setPropsLoading(true);
    try {
      const res = await analytics.properties();
      setGaProperties(res.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load properties");
    } finally {
      setPropsLoading(false);
    }
  }

  async function assignPropertyToSite(propertyId: string) {
    if (!site) return;
    setSavingProperty(true);
    try {
      await analytics.setSiteProperty(site._id, propertyId);
      setShowPropertyPicker(false);
      await loadSite();
      loadAnalyticsData(days, site._id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to set property");
    } finally {
      setSavingProperty(false);
    }
  }

  // ─── Process handlers ────────────────────────────────────

  function getScheduledTaskForProcess(process: TaskTemplate): Task | undefined {
    return scheduledList.find((t) => t.templateId === process._id);
  }

  async function handleToggleProcess(process: TaskTemplate) {
    if (!site) return;
    const existing = getScheduledTaskForProcess(process);

    try {
      if (existing) {
        // Toggle off — delete the scheduled task
        await scheduledTasksApi.delete(existing._id);
      } else {
        // Toggle on — create a scheduled task from this process
        const startDate = processStartDates[process._id] || new Date().toISOString().split("T")[0];
        await scheduledTasksApi.create({
          siteId: site._id,
          type: process.type,
          title: process.name,
          description: process.objective,
          cronExpression: process.cronExpression || "0 9 * * 1",
          enabled: true,
          templateId: process._id,
          ...(process.defaultAgentId ? { assignedAgentId: process.defaultAgentId } : {}),
          context: { startDate },
        });
      }
      await loadProcessesAndTasks();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to toggle process");
    }
  }

  async function handleRunNow(task: Task) {
    if (!site) return;
    setRunningTaskId(task._id);
    try {
      await scheduledTasksApi.run(task._id);
      startSession({
        title: `${task.title} — ${site.domain}`,
        message:
          `Run "${task.title}" for ${site.domain}.\n\n` +
          `Site: ${site.domain} (${site.config?.niche || "general"} niche)\n` +
          `Site ID: ${site._id}\n` +
          `Task: ${task.description || task.title}\n\n` +
          `Execute this task now and show your progress.`,
        ...(task.assignedAgentId ? { agentId: task.assignedAgentId } : {}),
      });
      await loadProcessesAndTasks();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to run task");
    } finally {
      setRunningTaskId(null);
    }
  }

  // ─── DNS ──────────────────────────────────────────────────

  const [dnsLoading, setDnsLoading] = useState(false);

  async function handleRetryDns() {
    if (!site) return;
    setDnsLoading(true);
    try {
      await sites.setupDns(site._id);
      await loadSite();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "DNS setup failed");
    } finally {
      setDnsLoading(false);
    }
  }

  // ─── Site import (Firecrawl) ───────────────────────────

  const [importing, setImporting] = useState(false);

  async function handleImportFromSite() {
    if (!site) return;
    const defaultUrl = site.config?.sourceUrl || `https://${site.domain}`;
    const sourceUrl = window.prompt(
      "Enter source URL to crawl and import:",
      defaultUrl
    );
    if (sourceUrl === null) return; // cancelled

    const limitStr = window.prompt("Max pages to import (default 100):", "100");
    const limit = limitStr ? parseInt(limitStr, 10) || 100 : 100;

    setImporting(true);
    try {
      // Save sourceUrl to site config for future re-imports
      if (sourceUrl && sourceUrl !== site.config?.sourceUrl) {
        await sites.update(site._id, {
          config: { ...site.config, sourceUrl },
        });
      }

      await agentTasks.importContent(site._id, {
        ...(sourceUrl ? { sourceUrl } : {}),
        limit,
        status: "draft",
      });
      startSession({
        title: `Import — ${site.domain}`,
        message:
          `Import content from ${sourceUrl || defaultUrl} into ${site.domain}.\n\n` +
          `Crawl up to ${limit} pages, extract content, and save as draft pages.\n\n` +
          `Site ID: ${site._id}\nDomain: ${site.domain}\nNiche: ${site.config?.niche || "general"}\n\n` +
          `Show progress as pages are imported.`,
      });
    } catch (err) {
      console.error("Import failed:", err);
      alert("Failed to start import");
    } finally {
      setImporting(false);
    }
  }

  // ─── Clear Content ─────────────────────────────────────────

  const [clearingContent, setClearingContent] = useState(false);

  const handleClearContent = async () => {
    if (!site) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete ALL content on ${site.domain}? This will permanently remove all pages from the database and edge cache. This cannot be undone.`
    );
    if (!confirmed) return;
    setClearingContent(true);
    try {
      const res = await sites.clearContent(site._id);
      alert(`Cleared ${res.data.deleted} pages from ${site.domain}`);
      // Refresh page list
      const pagesRes = await pages.list(site._id, 1, PAGE_SIZE);
      setPageList(pagesRes.data);
      setTotal(pagesRes.total);
    } catch (err) {
      console.error("Clear content failed:", err);
      alert("Failed to clear content");
    } finally {
      setClearingContent(false);
    }
  };

  // ─── Filtered pages ──────────────────────────────────────

  const filteredPages = pageList
    .filter((p) =>
      p.slug.toLowerCase().includes(search.toLowerCase()) ||
      p.frontmatter?.title?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      switch (sortCol) {
        case "slug":
          return dir * a.slug.localeCompare(b.slug);
        case "title":
          return dir * (a.frontmatter?.title ?? "").localeCompare(b.frontmatter?.title ?? "");
        case "words":
          return dir * (wordCount(a.content) - wordCount(b.content));
        case "updated":
          return dir * (new Date(a.updatedAt ?? 0).getTime() - new Date(b.updatedAt ?? 0).getTime());
        default:
          return 0;
      }
    });

  // ─── Render ──────────────────────────────────────────────

  if (siteLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-gray-600 animate-spin" />
      </div>
    );
  }

  if (!site) {
    return (
      <div className="max-w-5xl mx-auto py-12 text-center">
        <p className="text-sm text-gray-500">Site not found</p>
        <button onClick={() => navigate("/websites")} className="mt-4 text-sm text-brand-400 hover:text-brand-300">
          Back to Websites
        </button>
      </div>
    );
  }

  const siteHasProperty = !!(site.config?.gaPropertyId || connStatus?.propertyId);
  // no computed needed — processes are loaded from API

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Back + Header */}
      <div>
        <button
          onClick={() => navigate(site.clusterId ? `/businesses/${site.clusterId}` : "/businesses")}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-300 transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          {businessName ?? "Businesses"}
        </button>

        {businessName && site.clusterId && (
          <div className="flex items-center gap-1.5 text-sm text-gray-400 mb-1">
            <Link to="/businesses" className="hover:text-gray-200">Businesses</Link>
            <span>/</span>
            <Link to={`/businesses/${site.clusterId}`} className="hover:text-gray-200">{businessName}</Link>
            <span>/</span>
            <span className="text-gray-300">{site.domain}</span>
          </div>
        )}

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <a
              href={`https://${site.domain}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors"
              title={`Open ${site.domain}`}
            >
              <Globe className="w-5 h-5 text-brand-400" />
            </a>
            <div>
              <h1 className="text-lg font-bold text-gray-100">{site.domain}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[site.status] ?? STATUS_COLORS["pending"]}`}>
                  {site.status}
                </span>
                {cluster && (
                  cluster.hubDomain === site.domain
                    ? <span className="text-[10px] px-1.5 py-0.5 bg-brand-500/10 text-brand-400 rounded-full font-medium uppercase tracking-wide">Hub</span>
                    : <span className="text-[10px] px-1.5 py-0.5 bg-gray-700/50 text-gray-400 rounded-full font-medium uppercase tracking-wide">Spoke</span>
                )}
                {site.config?.niche && (
                  <span className="text-xs text-gray-500">{site.config.niche}</span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={openSiteEdit}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <Settings className="w-3.5 h-3.5" />
            Edit Site
          </button>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="border-b border-gray-800">
        <div className="flex gap-0">
          {[...BASE_TABS, ...((site?.config as unknown as Record<string, unknown>)?.renderEngine === "astro" || showBuildTab ? [{ key: "build" as TabKey, label: "Build" }] : [])].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === key
                  ? "border-brand-500 text-brand-400"
                  : "border-transparent text-gray-500 hover:text-gray-300"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-300">&times;</button>
        </div>
      )}

      {/* ═══════════════ OVERVIEW TAB ═══════════════ */}
      {activeTab === "overview" && (
        <div className="space-y-3">
          {/* Top row: stats + actions side by side */}
          <div className="flex items-start gap-3">
            {/* Compact stats */}
            <div className="flex-1 grid grid-cols-3 gap-2">
              {[
                { label: "Pages", value: String(site.stats?.pageCount ?? 0) },
                { label: "Status", value: site.status ?? "active" },
                { label: "Niche", value: site.config?.niche || "—" },
              ].map((s) => (
                <div key={s.label} className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide">{s.label}</p>
                  <p className="text-xs text-gray-200 truncate mt-0.5 capitalize">{s.value}</p>
                </div>
              ))}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                onClick={handleImportFromSite}
                disabled={importing}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-gray-200 text-xs font-medium rounded-lg transition-colors"
                title="Import Content"
              >
                {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                Import
              </button>
              <button
                onClick={handleClearContent}
                disabled={clearingContent}
                className="p-1.5 bg-red-900/40 hover:bg-red-800/50 border border-red-800/40 text-red-400 rounded-lg transition-colors disabled:opacity-50"
                title="Clear Content"
              >
                {clearingContent ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          {/* DNS (only if not fully configured) + Cluster row */}
          {(() => {
            const dnsConfigured = !!(site.config?.dnsStatus?.configured && site.config.dnsStatus.zoneStatus === "active");
            const showDns = !dnsConfigured;
            const showCluster = !!cluster;
            if (!showDns && !showCluster) return null;
            return (
              <div className={`grid gap-2 ${showDns && showCluster ? "grid-cols-2" : "grid-cols-1"}`}>
                {showDns && (
                  <div className={`bg-gray-900 border rounded-lg px-3 py-2.5 ${
                    site.config?.dnsStatus?.configured
                      ? "border-green-800/60"
                      : site.config?.dnsStatus?.error
                        ? "border-red-800/60"
                        : "border-gray-800"
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                          site.config?.dnsStatus?.configured
                            ? "bg-green-500/10 text-green-400"
                            : site.config?.dnsStatus?.error
                              ? "bg-red-500/10 text-red-400"
                              : "bg-amber-500/10 text-amber-400"
                        }`}>
                          {site.config?.dnsStatus?.configured ? (
                            <CheckCircle2 className="w-3 h-3" />
                          ) : site.config?.dnsStatus?.error ? (
                            <AlertTriangle className="w-3 h-3" />
                          ) : (
                            <Wifi className="w-3 h-3" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-200">DNS</p>
                          <p className="text-[10px] text-gray-500 truncate">
                            {site.config?.dnsStatus?.configured
                              ? "Configured — waiting for NS verification"
                              : site.config?.dnsStatus?.error
                                ? "Setup failed"
                                : "Not configured"}
                            {site.config?.dnsStatus?.pagesProject && ` · ${site.config.dnsStatus.pagesProject}.pages.dev`}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleRetryDns}
                        disabled={dnsLoading}
                        className="p-1 text-gray-500 hover:text-gray-300 hover:bg-gray-800 disabled:opacity-50 rounded transition-colors flex-shrink-0"
                        title={site.config?.dnsStatus?.configured ? "Refresh DNS" : "Setup DNS"}
                      >
                        {dnsLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      </button>
                    </div>
                    {site.config?.dnsStatus?.error && (
                      <p className="text-[10px] text-red-400 mt-1.5 truncate">{site.config.dnsStatus.error}</p>
                    )}
                    {site.config?.dnsStatus?.nameServers && site.config.dnsStatus.nameServers.length > 0 && site.config.dnsStatus.zoneStatus !== "active" && (
                      <div className="mt-1.5 text-[10px]">
                        <span className="text-amber-400">NS: </span>
                        <span className="text-gray-400 font-mono">{site.config.dnsStatus.nameServers.join(", ")}</span>
                      </div>
                    )}
                  </div>
                )}

                {showCluster && (
              <div className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2.5">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-6 h-6 rounded flex items-center justify-center bg-brand-500/10 text-brand-400 flex-shrink-0">
                    <Network className="w-3 h-3" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-200">{cluster.name}</p>
                    <p className="text-[10px] text-gray-500">
                      {cluster.hubDomain === site.domain ? "Hub" : `Spoke → ${cluster.hubDomain}`}
                      {" · "}
                      {cluster.interlinkStrategy === "hub-and-spoke" ? "Hub & Spoke" : cluster.interlinkStrategy === "mesh" ? "Mesh" : cluster.interlinkStrategy}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${cluster.hubDomain === site.domain ? "bg-brand-500/10 text-brand-300" : "bg-gray-800 text-gray-500"}`}>
                    {cluster.hubDomain}{cluster.hubDomain === site.domain ? " (you)" : ""}
                  </span>
                  {cluster.spokeDomains.map((d) => (
                    <span key={d} className={`px-1.5 py-0.5 rounded text-[10px] ${d === site.domain ? "bg-brand-500/10 text-brand-300" : "bg-gray-800 text-gray-500"}`}>
                      {d}{d === site.domain ? " (you)" : ""}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
            );
          })()}

          {/* Theme & Font Style */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2.5">
              <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">Color Theme</p>
              <div className="grid grid-cols-4 gap-1.5">
                {THEME_OPTIONS.map((t) => (
                  <button
                    key={t.value}
                    onClick={async () => {
                      if (!site) return;
                      await sites.update(site._id, { config: { ...site.config, theme: t.value } });
                      await loadSite();
                    }}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-[10px] transition-all ${
                      (site.config?.theme ?? "forest") === t.value
                        ? "ring-1 ring-brand-500 bg-gray-800 text-white"
                        : "bg-gray-800/50 text-gray-400 hover:bg-gray-800 hover:text-gray-300"
                    }`}
                    title={t.label}
                  >
                    <span className="w-3 h-3 rounded-full flex-shrink-0 ring-1 ring-white/10" style={{ background: t.swatch }} />
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2.5">
              <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">Font Style</p>
              <div className="grid grid-cols-1 gap-1">
                {FONT_OPTIONS.map((f) => (
                  <button
                    key={f.value}
                    onClick={async () => {
                      if (!site) return;
                      await sites.update(site._id, { config: { ...site.config, fontStyle: f.value } });
                      await loadSite();
                    }}
                    className={`flex items-center justify-between px-2 py-1 rounded-md text-[10px] transition-all ${
                      (site.config?.fontStyle ?? "modern") === f.value
                        ? "ring-1 ring-brand-500 bg-gray-800 text-white"
                        : "bg-gray-800/50 text-gray-400 hover:bg-gray-800 hover:text-gray-300"
                    }`}
                  >
                    <span className="font-medium">{f.label}</span>
                    <span className="text-gray-600">{f.sample}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Processes */}
          <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-800 flex items-center justify-between">
              <p className="text-xs font-semibold text-gray-200">Processes</p>
              <button
                onClick={() => navigate("/ai-studio?tab=templates")}
                className="text-[10px] text-gray-600 hover:text-brand-400 transition-colors"
              >
                Manage templates →
              </button>
            </div>

            {processesLoading ? (
              <div className="flex items-center justify-center h-16">
                <Loader2 className="w-4 h-4 text-gray-600 animate-spin" />
              </div>
            ) : processList.length === 0 ? (
              <div className="px-3 py-4 text-center">
                <p className="text-xs text-gray-500">No processes configured</p>
                <button
                  onClick={() => navigate("/ai-studio?tab=templates")}
                  className="text-[10px] text-brand-400 hover:text-brand-300 mt-1 transition-colors"
                >
                  Add process templates in AI Studio →
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-800/50">
                {processList.map((process) => {
                  const task = getScheduledTaskForProcess(process);
                  const isEnabled = !!task;
                  const cronLabel = CRON_LABELS[process.cronExpression ?? ""] ?? process.cronExpression ?? "Manual";
                  return (
                    <div key={process._id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-800/30 transition-colors">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <button
                          onClick={() => handleToggleProcess(process)}
                          className="flex-shrink-0"
                          title={isEnabled ? "Disable" : "Enable"}
                        >
                          {isEnabled ? (
                            <ToggleRight className="w-4 h-4 text-green-400" />
                          ) : (
                            <ToggleLeft className="w-4 h-4 text-gray-600" />
                          )}
                        </button>
                        <div className="min-w-0">
                          <p className={`text-xs font-medium ${isEnabled ? "text-gray-200" : "text-gray-500"}`}>{process.name}</p>
                          <div className="flex items-center gap-2 text-[10px] text-gray-600">
                            <span className="flex items-center gap-0.5">
                              <RefreshCw className="w-2.5 h-2.5" />
                              {cronLabel}
                            </span>
                            {task?.lastRun && (
                              <span>Last: {new Date(task.lastRun).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {!isEnabled && (
                          <input
                            type="date"
                            value={processStartDates[process._id] ?? ""}
                            onChange={(e) => setProcessStartDates((prev) => ({ ...prev, [process._id]: e.target.value }))}
                            className="px-1.5 py-0.5 bg-gray-800 border border-gray-700 rounded text-[10px] text-gray-400 focus:outline-none focus:ring-1 focus:ring-brand-500 w-28"
                            title="Start date"
                          />
                        )}
                        {isEnabled && task && (
                          <button
                            onClick={() => handleRunNow(task)}
                            disabled={runningTaskId === task._id}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] bg-brand-600/20 text-brand-400 hover:bg-brand-600/30 disabled:opacity-50 rounded transition-colors"
                          >
                            <Play className="w-2.5 h-2.5" />
                            {runningTaskId === task._id ? "..." : "Run"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════ CONTENT TAB ═══════════════ */}
      {activeTab === "content" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-gray-100 flex-shrink-0">Pages</h2>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search pages..."
                className="w-full pl-9 pr-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs rounded-lg transition-colors flex-shrink-0"
            >
              <Plus className="w-3.5 h-3.5" />
              New Page
            </button>
          </div>

          {/* Pages table */}
          {pagesLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-5 h-5 text-gray-600 animate-spin" />
            </div>
          ) : filteredPages.length === 0 ? (
            <div className="bg-gray-900 border border-gray-800 border-dashed rounded-xl p-10 text-center">
              <FileText className="w-8 h-8 text-gray-700 mx-auto mb-2" />
              <p className="text-sm text-gray-500">
                {search ? "No pages match your search" : "No pages yet"}
              </p>
            </div>
          ) : (
            <>
              <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      {([
                        { key: "slug" as const, label: "Slug", align: "left", hide: "" },
                        { key: "title" as const, label: "Title", align: "left", hide: "hidden md:table-cell" },
                        { key: "words" as const, label: "Words", align: "right", hide: "hidden sm:table-cell" },
                        { key: "updated" as const, label: "Updated", align: "right", hide: "hidden lg:table-cell" },
                      ]).map((col) => (
                        <th
                          key={col.key}
                          onClick={() => {
                            if (sortCol === col.key) {
                              setSortDir((d) => d === "asc" ? "desc" : "asc");
                            } else {
                              setSortCol(col.key);
                              setSortDir(col.key === "updated" || col.key === "words" ? "desc" : "asc");
                            }
                          }}
                          className={`text-${col.align} px-4 py-3 text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-300 select-none transition-colors ${col.hide}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {col.label}
                            {sortCol === col.key ? (
                              sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                            ) : (
                              <ArrowUpDown className="w-3 h-3 opacity-30" />
                            )}
                          </span>
                        </th>
                      ))}
                      <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPages.map((page, i) => (
                      <tr
                        key={page._id}
                        className={`group hover:bg-gray-800/40 transition-colors ${i < filteredPages.length - 1 ? "border-b border-gray-800/50" : ""}`}
                      >
                        <td className="px-4 py-3">
                          <a
                            href={`https://${site.domain}/${page.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-gray-300 hover:text-brand-400 font-mono text-xs transition-colors"
                          >
                            {page.slug}
                            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                          </a>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-gray-400 truncate max-w-xs block">{page.frontmatter?.title || "---"}</span>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-500 text-xs hidden sm:table-cell">
                          {wordCount(page.content).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-500 text-xs hidden lg:table-cell">
                          {page.updatedAt ? new Date(page.updatedAt).toLocaleDateString() : "---"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1 flex-wrap">
                            <button
                              onClick={async () => {
                                const srcBase = site.config?.sourceUrl || `https://${site.domain}`;
                                const srcUrl = `${srcBase.replace(/\/+$/, "")}/${page.slug.replace(/-/g, "/")}`;
                                try {
                                  setImportingPageId(page._id);
                                  const res = await agentTasks.importPage(site._id, page._id, srcUrl);
                                  // Refresh page list to show updated content
                                  loadPages(currentPage);
                                  alert(`Imported "${res.data.title}" (${res.data.wordCount} words)`);
                                } catch (err: unknown) {
                                  alert(err instanceof Error ? err.message : "Import failed");
                                } finally {
                                  setImportingPageId(null);
                                }
                              }}
                              disabled={importingPageId === page._id}
                              className="p-1.5 text-gray-500 hover:text-blue-400 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                              title={`Import from ${site.config?.sourceUrl || site.domain}`}
                            >
                              {importingPageId === page._id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RotateCw className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => {
                                startSession({
                                  title: `Create /${page.slug}`,
                                  message:
                                    `Generate new content for the page "/${page.slug}" on ${site.domain}.\n\n` +
                                    `Title: ${page.frontmatter?.title || page.slug}\n` +
                                    `Keywords: ${page.frontmatter?.keywords?.join(", ") || "none"}\n` +
                                    `Niche: ${site.config?.niche || "general"}\n\n` +
                                    `Site ID: ${site._id}\nPage ID: ${page._id}\nSlug: ${page.slug}\n\n` +
                                    `Write high-quality affiliate content for this page and save it.`,
                                });
                              }}
                              className="p-1.5 text-gray-500 hover:text-purple-400 hover:bg-gray-800 rounded-lg transition-colors"
                              title="Generate content with AI"
                            >
                              <Sparkles className="w-4 h-4" />
                            </button>
                            <button onClick={() => openEdit(page)} className="p-1.5 text-gray-500 hover:text-gray-300 hover:bg-gray-800 rounded-lg transition-colors" title="Edit">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => setDeleteId(page._id)} className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-gray-800 rounded-lg transition-colors" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">Page {currentPage} / {total} total</p>
                <div className="flex gap-2">
                  <button onClick={() => loadPages(currentPage - 1)} disabled={currentPage <= 1} className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200 hover:bg-gray-800 disabled:opacity-40 rounded-lg transition-colors">
                    <ChevronLeft className="w-3.5 h-3.5" /> Prev
                  </button>
                  <button onClick={() => loadPages(currentPage + 1)} disabled={!hasMore} className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-400 hover:text-gray-200 hover:bg-gray-800 disabled:opacity-40 rounded-lg transition-colors">
                    Next <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══════════════ ANALYTICS TAB ═══════════════ */}
      {activeTab === "analytics" && (
        <div className="space-y-6">
          {analyticsLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !connStatus?.connected || !connStatus.accountId ? (
            /* Connect / account prompt — redirect to Settings */
            <div className="max-w-lg mx-auto mt-12 text-center">
              <div className="w-16 h-16 bg-brand-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <BarChart3 className="w-8 h-8 text-brand-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-100 mb-2">
                {connStatus?.connected ? "Select Analytics Account" : "Connect Google Analytics"}
              </h2>
              <p className="text-sm text-gray-400 mb-6">
                {connStatus?.connected
                  ? "Go to Settings to choose which Google Analytics account your websites belong to."
                  : "Go to Settings to link your Google Analytics account first."}
              </p>
              <button
                onClick={() => navigate("/settings")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg transition-colors"
              >
                <Settings className="w-4 h-4" />
                Go to Settings
              </button>
            </div>
          ) : (
            <>
              {/* Analytics header controls */}
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-gray-100">Site Analytics</h2>
                  <p className="text-xs text-gray-500 mt-0.5">{site.domain}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={openPropertyPicker}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-400 hover:text-brand-400 hover:bg-gray-800 rounded-lg transition-colors"
                    title="Change GA property"
                  >
                    <Settings2 className="w-3.5 h-3.5" />
                    Property
                  </button>

                  <div className="relative">
                    <select
                      value={days}
                      onChange={(e) => handlePeriodChange(Number(e.target.value))}
                      className="appearance-none bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-lg pl-3 pr-8 py-2 focus:ring-brand-500 focus:border-brand-500"
                    >
                      <option value={7}>Last 7 days</option>
                      <option value={14}>Last 14 days</option>
                      <option value={30}>Last 30 days</option>
                      <option value={90}>Last 90 days</option>
                    </select>
                    <ChevronDown className="w-3 h-3 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>

                  <button
                    onClick={() => loadAnalyticsData(days, site._id)}
                    disabled={dataLoading}
                    className="p-2 rounded-lg text-gray-400 hover:text-gray-100 hover:bg-gray-800 transition-colors disabled:opacity-50"
                    title="Refresh"
                  >
                    <RefreshCw className={`w-4 h-4 ${dataLoading ? "animate-spin" : ""}`} />
                  </button>

                  <button
                    onClick={() => navigate("/settings")}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors"
                    title="Manage GA connection in Settings"
                  >
                    <Settings className="w-3.5 h-3.5" />
                    Manage
                  </button>
                </div>
              </div>

              {/* No property assigned — offer auto-provision or manual */}
              {!siteHasProperty ? (
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-5 text-center">
                  <p className="text-sm text-gray-400 mb-3">
                    No GA4 property linked to {site.domain}.
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={async () => {
                        try {
                          await analytics.autoProvision(site._id);
                          await loadSite();
                          loadAnalyticsData(days, site._id);
                        } catch (err: unknown) {
                          setError(err instanceof Error ? err.message : "Auto-provision failed");
                        }
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium rounded-lg transition-colors"
                    >
                      <Play className="w-3.5 h-3.5" />
                      Create Property
                    </button>
                    <button
                      onClick={openPropertyPicker}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-medium rounded-lg transition-colors"
                    >
                      <Link2 className="w-3.5 h-3.5" />
                      Assign Existing
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Overview stats */}
                  {overview && (
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                      <StatCard icon={Users} label="Users" value={overview.totalUsers.toLocaleString()} color="bg-brand-500/10 text-brand-400" />
                      <StatCard icon={BarChart3} label="Sessions" value={overview.totalSessions.toLocaleString()} color="bg-purple-500/10 text-purple-400" />
                      <StatCard icon={Eye} label="Pageviews" value={overview.totalPageviews.toLocaleString()} color="bg-green-500/10 text-green-400" />
                      <StatCard icon={ArrowDownRight} label="Bounce Rate" value={`${(overview.avgBounceRate * 100).toFixed(1)}%`} color="bg-amber-500/10 text-amber-400" />
                      <StatCard icon={Clock} label="Avg Duration" value={fmtDuration(overview.avgSessionDuration)} color="bg-cyan-500/10 text-cyan-400" />
                    </div>
                  )}

                  {/* Charts */}
                  {daily.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                        <h3 className="text-sm font-medium text-gray-400 mb-1">Users</h3>
                        <p className="text-lg font-bold text-gray-100 mb-2">
                          {daily.reduce((s, d) => s + d.users, 0).toLocaleString()}
                        </p>
                        <MiniBarChart data={daily} dataKey="users" color="bg-brand-500" />
                      </div>
                      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                        <h3 className="text-sm font-medium text-gray-400 mb-1">Pageviews</h3>
                        <p className="text-lg font-bold text-gray-100 mb-2">
                          {daily.reduce((s, d) => s + d.pageviews, 0).toLocaleString()}
                        </p>
                        <MiniBarChart data={daily} dataKey="pageviews" color="bg-green-500" />
                      </div>
                    </div>
                  )}

                  {/* Top pages + traffic sources */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-4 border-b border-gray-800">
                        <h3 className="text-sm font-medium text-gray-300">Top Pages</h3>
                      </div>
                      {topPages.length === 0 ? (
                        <p className="text-sm text-gray-500 p-5">No page data yet.</p>
                      ) : (
                        <div className="divide-y divide-gray-800/50">
                          {topPages.slice(0, 10).map((page, i) => (
                            <div key={i} className="flex items-center justify-between px-5 py-3 hover:bg-gray-800/30 transition-colors">
                              <div className="min-w-0 flex-1 mr-4">
                                <p className="text-sm text-gray-200 truncate">{page.path}</p>
                                <p className="text-xs text-gray-500 truncate">{page.title}</p>
                              </div>
                              <div className="flex items-center gap-4 text-xs text-gray-400 flex-shrink-0">
                                <span>{page.pageviews.toLocaleString()} views</span>
                                <span>{page.users.toLocaleString()} users</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-4 border-b border-gray-800">
                        <h3 className="text-sm font-medium text-gray-300">Traffic Sources</h3>
                      </div>
                      {trafficSources.length === 0 ? (
                        <p className="text-sm text-gray-500 p-5">No traffic source data yet.</p>
                      ) : (
                        <div className="divide-y divide-gray-800/50">
                          {trafficSources.map((src, i) => {
                            const totalSessions = trafficSources.reduce((s, t) => s + t.sessions, 0);
                            const pct = totalSessions > 0 ? (src.sessions / totalSessions) * 100 : 0;
                            return (
                              <div key={i} className="px-5 py-3 hover:bg-gray-800/30 transition-colors">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-200">{src.source}</span>
                                    <span className="text-xs text-gray-500">/ {src.medium}</span>
                                  </div>
                                  <span className="text-xs text-gray-400">{src.sessions.toLocaleString()} sessions</span>
                                </div>
                                <div className="w-full bg-gray-800 rounded-full h-1.5">
                                  <div className="bg-brand-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Affiliate Click Stats */}
                  {clickStats && clickStats.totalClicks > 0 && (
                    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
                        <h3 className="text-sm font-medium text-gray-300 flex items-center gap-2">
                          <ShoppingCart className="w-4 h-4 text-green-400" />
                          Affiliate Clicks
                        </h3>
                        <span className="text-xs text-gray-500">{clickStats.totalClicks.toLocaleString()} total</span>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-800">
                        {/* Top pages by clicks */}
                        <div>
                          <p className="px-5 py-2 text-xs text-gray-500 font-medium uppercase tracking-wide">Top Pages</p>
                          <div className="divide-y divide-gray-800/50">
                            {clickStats.topPages.slice(0, 5).map((p, i) => (
                              <div key={i} className="flex items-center justify-between px-5 py-2.5 hover:bg-gray-800/30 transition-colors">
                                <span className="text-sm text-gray-300 truncate flex-1 mr-3">/{p.slug || "home"}</span>
                                <span className="text-xs text-gray-400 flex-shrink-0">{p.clicks} clicks</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Top networks by clicks */}
                        <div>
                          <p className="px-5 py-2 text-xs text-gray-500 font-medium uppercase tracking-wide">By Network</p>
                          <div className="divide-y divide-gray-800/50">
                            {clickStats.topNetworks.map((n, i) => {
                              const total = clickStats.totalClicks;
                              const pct = total > 0 ? (n.clicks / total) * 100 : 0;
                              const networkColor: Record<string, string> = { amazon: "bg-amber-500", ebay: "bg-red-500", awin: "bg-orange-500", cj: "bg-green-500" };
                              const barColor = networkColor[n.network.toLowerCase()] ?? "bg-brand-500";
                              return (
                                <div key={i} className="px-5 py-2.5 hover:bg-gray-800/30 transition-colors">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm text-gray-300 capitalize">{n.network}</span>
                                    <span className="text-xs text-gray-400">{n.clicks} ({pct.toFixed(0)}%)</span>
                                  </div>
                                  <div className="w-full bg-gray-800 rounded-full h-1.5">
                                    <div className={`${barColor} h-1.5 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Property picker modal */}
              {showPropertyPicker && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowPropertyPicker(false)}>
                  <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
                    <h3 className="text-lg font-bold text-gray-100 mb-1">Assign Property to {site.domain}</h3>
                    <p className="text-sm text-gray-400 mb-4">Choose the GA4 property for this site.</p>
                    {propsLoading ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : gaProperties.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-6">No GA4 properties found.</p>
                    ) : (
                      <div className="space-y-2 max-h-72 overflow-y-auto">
                        {gaProperties.map((prop) => (
                          <button
                            key={prop.name}
                            onClick={() => assignPropertyToSite(prop.name)}
                            disabled={savingProperty}
                            className="w-full flex items-center justify-between px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg hover:border-brand-500/50 transition-colors text-left disabled:opacity-50"
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-100">{prop.displayName}</p>
                              <p className="text-xs text-gray-500">{prop.name}</p>
                            </div>
                            <ExternalLink className="w-3.5 h-3.5 text-gray-500" />
                          </button>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => setShowPropertyPicker(false)}
                      className="mt-4 w-full py-2 text-sm text-gray-400 hover:text-gray-200 bg-gray-800 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══════════════ IMPORT TAB ═══════════════ */}
      {activeTab === "import" && (
        <ImportTab siteId={site._id} domain={site.domain} sourceUrl={site.config?.sourceUrl} onImportDone={() => loadPages(1)} />
      )}

      {/* ═══════════════ ACTIONS TAB ═══════════════ */}
      {activeTab === "actions" && (
        <ActionsTab siteId={id!} domain={site.domain} />
      )}

      {/* ═══════════════ AFFILIATES TAB ═══════════════ */}
      {activeTab === "affiliates" && site && (
        <SiteAffiliatesTab site={site} onSaved={loadSite} />
      )}

      {/* ═══════════════ SEO TAB ═══════════════ */}
      {activeTab === "seo" && site && (
        <SeoTab siteId={site._id} domain={site.domain} />
      )}

      {/* ═══════════════ PUBLISHING TAB ═══════════════ */}
      {activeTab === "publishing" && site && (
        <PublishingTab siteId={site._id} tenantCap={tenantCap} />
      )}

      {/* ═══════════════ RUNS TAB ═══════════════ */}
      {activeTab === "runs" && site && (
        <RunsTab siteId={site._id} />
      )}

      {/* ═══════════════ BUILD TAB ═══════════════ */}
      {activeTab === "build" && site && (
        <BuildTab
          siteId={site._id}
          siteConfig={site.config}
          buildError={buildError}
          setBuildError={setBuildError}
          activeBuildTaskId={activeBuildTaskId}
          loadSite={loadSite}
          buildDeploying={buildDeploying}
          setBuildDeploying={setBuildDeploying}
          buildRegenerating={buildRegenerating}
          setBuildRegenerating={setBuildRegenerating}
        />
      )}

      {/* ═══════════════ MODALS ═══════════════ */}

      {/* Page Editor Modal */}
      <Modal open={editor.open} onClose={() => setEditor({ open: false, page: null })} title={editor.page ? `Edit: ${editor.page.slug}` : "New Page"} size="xl">
        <form onSubmit={handleSave} className="space-y-4">
          {editorError && (
            <div className="px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">{editorError}</div>
          )}
          <div className="grid grid-cols-2 gap-3">
            {!editor.page && (
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Slug *</label>
                <input value={editorSlug} onChange={(e) => setEditorSlug(e.target.value)} required placeholder="my-page-slug" className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm font-mono text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
            )}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Title</label>
              <input value={editorFrontmatter.title} onChange={(e) => setEditorFrontmatter((f) => ({ ...f, title: e.target.value }))} placeholder="Page title" className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Description</label>
              <input value={editorFrontmatter.description} onChange={(e) => setEditorFrontmatter((f) => ({ ...f, description: e.target.value }))} placeholder="Meta description" className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Keywords <span className="text-gray-600">(comma separated)</span></label>
              <input value={editorFrontmatter.keywords?.join(", ") ?? ""} onChange={(e) => setEditorFrontmatter((f) => ({ ...f, keywords: e.target.value.split(",").map((k) => k.trim()).filter(Boolean) }))} placeholder="keyword one, keyword two" className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500" />
            </div>
          </div>

          {/* Editor toolbar */}
          <div className="flex items-center justify-between">
            <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
              {(["edit", "split", "preview"] as const).map((v) => (
                <button key={v} type="button" onClick={() => setEditorView(v)} className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors ${editorView === v ? "bg-gray-700 text-gray-100" : "text-gray-500 hover:text-gray-300"}`}>
                  {v === "edit" && <Code2 className="w-3.5 h-3.5" />}
                  {v === "split" && <FileText className="w-3.5 h-3.5" />}
                  {v === "preview" && <Eye className="w-3.5 h-3.5" />}
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Editor panes */}
          <div className={`grid gap-3 ${editorView === "split" ? "grid-cols-2" : "grid-cols-1"}`}>
            {(editorView === "edit" || editorView === "split") && (
              <textarea value={editorContent} onChange={(e) => setEditorContent(e.target.value)} rows={16} placeholder="# Page Title&#10;&#10;Write markdown content here..." className="w-full px-3 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm font-mono text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y" />
            )}
            {(editorView === "preview" || editorView === "split") && (
              <div className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg overflow-y-auto max-h-72">
                <div className="prose prose-sm prose-invert max-w-none">
                  {editorContent ? <ReactMarkdown>{editorContent}</ReactMarkdown> : <p className="text-gray-600 text-sm italic">Preview will appear here</p>}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setEditor({ open: false, page: null })} className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
              <Save className="w-3.5 h-3.5" />
              {saving ? "Saving..." : editor.page ? "Save changes" : "Create page"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Site Edit Modal */}
      <Modal open={showSiteEdit} onClose={() => setShowSiteEdit(false)} title={`Edit: ${site.domain}`} size="lg">
        {siteForm && (
          <form onSubmit={handleSiteSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Status</label>
                <select value={siteForm.status} onChange={(e) => setSiteForm((f) => f ? { ...f, status: e.target.value } : f)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Niche</label>
                <input value={siteForm.config.niche} onChange={(e) => setSiteForm((f) => f ? { ...f, config: { ...f.config, niche: e.target.value } } : f)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Title</label>
                <input value={siteForm.config.title} onChange={(e) => setSiteForm((f) => f ? { ...f, config: { ...f.config, title: e.target.value } } : f)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Description</label>
                <textarea value={siteForm.config.description} onChange={(e) => setSiteForm((f) => f ? { ...f, config: { ...f.config, description: e.target.value } } : f)} rows={2} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Template</label>
                <select value={(siteForm.config as unknown as Record<string, unknown>).templateName as string ?? ""} onChange={(e) => setSiteForm((f) => f ? { ...f, config: { ...f.config, templateName: e.target.value || undefined } as SiteConfig } : f)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500">
                  <option value="">Auto-detect</option>
                  <option value="site-template">Site Template (Service Business)</option>
                  <option value="chiro-site-template">Chiro Template</option>
                  <option value="saas-site-template">SaaS Template</option>
                  <option value="affiliate-template">Affiliate Template</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Deploy Status</label>
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm">
                  {(siteForm.config as unknown as Record<string, unknown>).astro ? (
                    <>
                      <span className={`w-2 h-2 rounded-full ${((siteForm.config as unknown as Record<string, unknown>).astro as Record<string, unknown>)?.deployStatus === "deployed" ? "bg-green-500" : ((siteForm.config as unknown as Record<string, unknown>).astro as Record<string, unknown>)?.deployStatus === "building" ? "bg-yellow-500 animate-pulse" : ((siteForm.config as unknown as Record<string, unknown>).astro as Record<string, unknown>)?.deployStatus === "failed" ? "bg-red-500" : "bg-gray-500"}`}></span>
                      <span className="text-gray-300 capitalize">{(((siteForm.config as unknown as Record<string, unknown>).astro as Record<string, unknown>)?.deployStatus as string | undefined) ?? "not deployed"}</span>
                    </>
                  ) : (
                    <span className="text-gray-500">Not deployed</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowSiteEdit(false)} className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors">Cancel</button>
              <button type="submit" disabled={siteSaving} className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                {siteSaving ? "Saving..." : "Save changes"}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Delete confirm */}
      <Modal open={!!deleteId} onClose={() => setDeleteId(null)} title="Delete Page" size="sm">
        <p className="text-sm text-gray-400 mb-5">Are you sure? This page and all its Q&A will be permanently deleted.</p>
        <div className="flex justify-end gap-3">
          <button onClick={() => setDeleteId(null)} className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded-lg transition-colors">Cancel</button>
          <button onClick={handleDelete} disabled={deleting} className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </Modal>
    </div>
  );
}
